// GPolygenesis.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './gpolygenesis.css';
import './gpolygenesis-light.css';
import { ensureGPolygenesisCookie } from './WebserverUser';
import {
  createServiceUpload,
  defaultServicePipeline,
  loadServiceCatalogue,
  loadServiceJobs,
  openJobDownload,
} from '../shared/xomics-webserver-client';
import CentralApiGuide from '../shared/CentralApiGuide';

import logoDark from '../../POLYGEN.png';
import logoLight from '../../POLYGEN_Dark.png';
import baseLogo from '../../base_logo.png';
import baseLogoLight from '../../base_logo_light.png';

const THEME_STORAGE_KEY = 'gpolygenesis-theme-preference';
const SERVICE_CODE = 'gpolygenesis';

// Apply theme immediately on script load to prevent flash
(function applyStoredThemeImmediately() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  let theme = 'dark';
  if (stored === 'light') {
    theme = 'light';
  } else if (stored === 'system') {
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    theme = prefersDark ? 'dark' : 'light';
  }
  const root = document.documentElement;
  root.classList.remove('gpolygenesis-theme-light', 'gpolygenesis-theme-dark');
  root.classList.add(theme === 'light' ? 'gpolygenesis-theme-light' : 'gpolygenesis-theme-dark');
})();

function GPolygenesis() {
  const [cookieId, setCookieId] = useState('');
  const [theme, setTheme] = useState('dark');
  const [activeTab, setActiveTab] = useState('home');

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Upload state
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pipeline, setPipeline] = useState(null);
  const [uploadUnavailable, setUploadUnavailable] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    ensureGPolygenesisCookie()
      .then((cookie) => setCookieId(cookie.id))
      .catch((error) => setUploadUnavailable(error.message));

    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) setTheme(stored);
  }, []);

  useEffect(() => {
    if (!cookieId) return;
    loadServiceCatalogue(SERVICE_CODE, cookieId)
      .then((catalogue) => {
        const selected = defaultServicePipeline(catalogue);
        setPipeline(selected);
        if (!selected.sample_codes?.length) {
          setUploadUnavailable('The active G-Polygenesis pipeline has no XOmics sample-type mapping. Existing centralized jobs remain available, but new submissions are disabled.');
        } else {
          setUploadUnavailable('');
        }
      })
      .catch((error) => setUploadUnavailable(error.message));
  }, [cookieId]);

// Time formatting helpers
 const getElapsed = (startedAt) => {
  if (!startedAt) return '';
  const utcString = startedAt.endsWith('Z') ? startedAt : startedAt + 'Z';
  const seconds = Math.floor((Date.now() - new Date(utcString)) / 1000);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const getDuration = (startedAt, finishedAt) => {
  if (!startedAt || !finishedAt) return '';
  const start = startedAt.endsWith('Z') ? startedAt : startedAt + 'Z';
  const end = finishedAt.endsWith('Z') ? finishedAt : finishedAt + 'Z';
  const seconds = Math.floor((new Date(end) - new Date(start)) / 1000);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

  const getStatusMessage = (job) => {
    switch (job.status) {
      case 'pending_upload': return 'Waiting for upload...';
      case 'queued': return 'In queue';
      case 'running': return `Processing... ${getElapsed(job.started_at)}`;
      case 'completed': return `Done (${getDuration(job.started_at, job.finished_at)})`;
      case 'failed': return job.message || 'Failed';
      default: return job.status;
    }
  };

  // Fetch jobs
  const fetchJobs = useCallback(async (showLoading = true) => {
    if (showLoading) setIsLoadingJobs(true);
    try {
      if (!cookieId) {
        setJobs([]);
        if (showLoading) setIsLoadingJobs(false);
        return;
      }
      const data = await loadServiceJobs(SERVICE_CODE, cookieId, { limit: 50 });
      setJobs(data.jobs.map((job) => ({
        ...job,
        uuid: job.job_uuid || job.upload_uuid,
        name: job.sample_name,
      })));
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setJobs([]);
    }
    if (showLoading) setIsLoadingJobs(false);
  }, [cookieId]);

  // Initial fetch
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Poll for running/queued jobs every 5 seconds
  useEffect(() => {
    const hasActiveJobs = jobs.some(job => job.status === 'queued' || job.status === 'running');
    if (!hasActiveJobs) return;

    const interval = setInterval(() => {
      fetchJobs(false);
    }, 5000);

    return () => clearInterval(interval);
  }, [jobs, fetchJobs]);

  const handleThemeToggle = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);

    const root = document.documentElement;
    root.classList.remove('gpolygenesis-theme-light', 'gpolygenesis-theme-dark');
    root.classList.add(newTheme === 'light' ? 'gpolygenesis-theme-light' : 'gpolygenesis-theme-dark');
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const acceptedExtensions = ['.tsv', '.tsv.gz', '.txt', '.txt.gz', '.csv', '.csv.gz', '.gz'];
    const validFiles = files.filter(file => {
      const ext = file.name.toLowerCase();
      return acceptedExtensions.some(suffix => ext.endsWith(suffix));
    });

    if (validFiles.length !== files.length) {
      alert('Some files were skipped. Only TSV, TXT, CSV, and compressed GWAS summary statistic files are accepted.');
    }

    setUploadedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (uploadedFiles.length === 0 || !pipeline || uploadUnavailable) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        await createServiceUpload({
          serviceCode: SERVICE_CODE,
          token: cookieId,
          pipeline,
          sampleCode: pipeline.sample_codes[0],
          selectedFiles: [{ file, slotPosition: 1, label: 'GWAS summary statistics' }],
          displayName: file.name,
          metadata: { original_filename: file.name },
          onProgress: ({ fraction }) => {
            setUploadProgress(((i + fraction) / uploadedFiles.length) * 100);
          },
        });
        setUploadProgress(((i + 1) / uploadedFiles.length) * 100);
      }

      setUploadedFiles([]);
      fetchJobs();
    } catch (err) {
      console.error('Upload error:', err);
      alert(err.message);
    }

    setIsUploading(false);
    setUploadProgress(0);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownload = async (job) => {
    try {
      await openJobDownload(SERVICE_CODE, cookieId, job);
    } catch (err) {
      console.error('Download error:', err);
      alert(err.message);
    }
  };

  const themeClass = theme === 'light' ? 'light-theme' : '';
  const logoSrc = theme === 'light' ? logoLight : logoDark;
  const baseLogoSrc = theme === 'light' ? baseLogoLight : baseLogo;

  return (
    <div className={`GPOLYGENESIS-app-container ${themeClass}`}>
      {/* AppBar Header */}
      <header className="GPOLYGENESIS-header">
        <div className="GPOLYGENESIS-header-left">
          <a href="/" className="GPOLYGENESIS-logo-container">
            <img
              src={baseLogoSrc}
              alt="Home"
              className="GPOLYGENESIS-logo-img"
            />
          </a>
          <nav className="GPOLYGENESIS-nav-tabs">
            <button
              className={`GPOLYGENESIS-nav-tab ${activeTab === 'home' ? 'active' : ''}`}
              onClick={() => setActiveTab('home')}
            >
              Home
            </button>
            <button
              className={`GPOLYGENESIS-nav-tab ${activeTab === 'api' ? 'active' : ''}`}
              onClick={() => setActiveTab('api')}
            >
              API
            </button>
            <button
              className={`GPOLYGENESIS-nav-tab ${activeTab === 'docs' ? 'active' : ''}`}
              onClick={() => setActiveTab('docs')}
            >
              Docs
            </button>
            <button
              className={`GPOLYGENESIS-nav-tab ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              Settings
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="GPOLYGENESIS-content-area">
        {activeTab === 'docs' ? (
          <div className="GPOLYGENESIS-docs-container">
            <div className="GPOLYGENESIS-docs-content">
              <div className="GPOLYGENESIS-docs-header">
                <h1>Documentation</h1>
                <p>Learn how to use G-Polygenesis for GWAS-to-PGS generation</p>
              </div>

              {/* Quick Start */}
              <div className="GPOLYGENESIS-docs-section">
                <div className="GPOLYGENESIS-docs-section-header">
                  <i className="fas fa-rocket"></i>
                  <h2>Quick Start</h2>
                </div>
                <div className="GPOLYGENESIS-docs-steps">
                  <div className="GPOLYGENESIS-docs-step">
                    <div className="GPOLYGENESIS-docs-step-num">1</div>
                    <div className="GPOLYGENESIS-docs-step-content">
                      <h3>Upload your GWAS summary statistics</h3>
                      <p>Drag and drop your GWAS file onto the upload area, or click to browse. We accept <code>.tsv</code>, <code>.txt</code>, <code>.csv</code>, and compressed files up to 1 GB.</p>
                    </div>
                  </div>
                  <div className="GPOLYGENESIS-docs-step">
                    <div className="GPOLYGENESIS-docs-step-num">2</div>
                    <div className="GPOLYGENESIS-docs-step-content">
                      <h3>Start the analysis</h3>
                      <p>Click "Start Analysis" to submit your file. Your job will be queued and processed automatically using the G-Polygenesis pipeline.</p>
                    </div>
                  </div>
                  <div className="GPOLYGENESIS-docs-step">
                    <div className="GPOLYGENESIS-docs-step-num">3</div>
                    <div className="GPOLYGENESIS-docs-step-content">
                      <h3>Download results</h3>
                      <p>Once complete, click the download button next to your job to get generated PGS weights and run metadata.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* What is G-Polygenesis */}
              <div className="GPOLYGENESIS-docs-section">
                <div className="GPOLYGENESIS-docs-section-header">
                  <i className="fas fa-dna"></i>
                  <h2>What is G-Polygenesis?</h2>
                </div>
                <p className="GPOLYGENESIS-docs-text">
                  G-Polygenesis generates polygenic score weights directly from GWAS summary statistics using the trained G-Polygenesis model. It is designed for one-pass PGS generation with tracked jobs, downloads, and API access.
                </p>
                <div className="GPOLYGENESIS-docs-features">
                  <div className="GPOLYGENESIS-docs-feature">
                    <i className="fas fa-bolt"></i>
                    <span>One-pass PGS generation</span>
                  </div>
                  <div className="GPOLYGENESIS-docs-feature">
                    <i className="fas fa-shield-alt"></i>
                    <span>Tracked upload and download jobs</span>
                  </div>
                  <div className="GPOLYGENESIS-docs-feature">
                    <i className="fas fa-cloud"></i>
                    <span>Web and API access</span>
                  </div>
                </div>
              </div>

              {/* Job Statuses */}
              <div className="GPOLYGENESIS-docs-section">
                <div className="GPOLYGENESIS-docs-section-header">
                  <i className="fas fa-tasks"></i>
                  <h2>Understanding Job Statuses</h2>
                </div>
                <div className="GPOLYGENESIS-docs-status-list">
                  <div className="GPOLYGENESIS-docs-status-item">
                    <span className="GPOLYGENESIS-status-badge queued">queued</span>
                    <span>Your job is waiting in the processing queue</span>
                  </div>
                  <div className="GPOLYGENESIS-docs-status-item">
                    <span className="GPOLYGENESIS-status-badge running">running</span>
                    <span>Your file is being processed</span>
                  </div>
                  <div className="GPOLYGENESIS-docs-status-item">
                    <span className="GPOLYGENESIS-status-badge completed">completed</span>
                    <span>Processing finished - download your results</span>
                  </div>
                  <div className="GPOLYGENESIS-docs-status-item">
                    <span className="GPOLYGENESIS-status-badge failed">failed</span>
                    <span>An error occurred - check the error message</span>
                  </div>
                </div>
              </div>

              {/* Self-Hosting */}
              <div className="GPOLYGENESIS-docs-section">
                <div className="GPOLYGENESIS-docs-section-header">
                  <i className="fas fa-server"></i>
                  <h2>Run Locally</h2>
                </div>
                <p className="GPOLYGENESIS-docs-text">
                  The same model can be run locally from the G-Polygenesis release package.
                </p>
              </div>

              {/* Support */}
              <div className="GPOLYGENESIS-docs-section GPOLYGENESIS-docs-support">
                <div className="GPOLYGENESIS-docs-section-header">
                  <i className="fas fa-life-ring"></i>
                  <h2>Need Help?</h2>
                </div>
                <p className="GPOLYGENESIS-docs-text">
                  For technical support, questions, feature requests, or feedback, reach out to our team:
                </p>
                <a href="mailto:hello@phenomelongevity.com" className="GPOLYGENESIS-docs-email-link">
                  <i className="fas fa-envelope"></i>
                  hello@phenomelongevity.com
                </a>
              </div>

            </div>
          </div>
        ) : activeTab === 'settings' ? (
          <div className="GPOLYGENESIS-settings-container">
            <div className="GPOLYGENESIS-settings-content">
              <div className="GPOLYGENESIS-settings-header">
                <h1>Settings</h1>
                <p>Configure your G-Polygenesis experience</p>
              </div>

              <div className="GPOLYGENESIS-settings-grid">
                <div className="GPOLYGENESIS-settings-section">
                  <div className="GPOLYGENESIS-settings-section-header">
                    <div className="GPOLYGENESIS-settings-section-icon">
                      <i className="fas fa-palette"></i>
                    </div>
                    <div>
                      <h2>Appearance</h2>
                      <p>Customize the look and feel</p>
                    </div>
                  </div>
                  <div className="GPOLYGENESIS-settings-section-content">
                    <div className="GPOLYGENESIS-setting-row">
                      <span className="GPOLYGENESIS-setting-label">Theme</span>
                      <div className="GPOLYGENESIS-theme-toggle">
                        <button
                          className={`GPOLYGENESIS-theme-btn ${theme === 'dark' ? 'active' : ''}`}
                          onClick={() => handleThemeToggle('dark')}
                        >
                          <i className="fas fa-moon"></i>
                        </button>
                        <button
                          className={`GPOLYGENESIS-theme-btn ${theme === 'light' ? 'active' : ''}`}
                          onClick={() => handleThemeToggle('light')}
                        >
                          <i className="fas fa-sun"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="GPOLYGENESIS-settings-section">
                  <div className="GPOLYGENESIS-settings-section-header">
                    <div className="GPOLYGENESIS-settings-section-icon">
                      <i className="fas fa-user-circle"></i>
                    </div>
                    <div>
                      <h2>Account</h2>
                      <p>Your session information</p>
                    </div>
                  </div>
                  <div className="GPOLYGENESIS-settings-section-content">
                    <div className="GPOLYGENESIS-setting-row">
                      <span className="GPOLYGENESIS-setting-label">Account ID</span>
                      <span className="GPOLYGENESIS-setting-value">{cookieId || 'N/A'}</span>
                    </div>
                    <div className="GPOLYGENESIS-setting-row">
                      <span className="GPOLYGENESIS-setting-label">Session</span>
                      <span className="GPOLYGENESIS-setting-value status-active">Active</span>
                    </div>
                  </div>
                </div>

                <div className="GPOLYGENESIS-settings-section">
                  <div className="GPOLYGENESIS-settings-section-header">
                    <div className="GPOLYGENESIS-settings-section-icon">
                      <i className="fas fa-info-circle"></i>
                    </div>
                    <div>
                      <h2>About</h2>
                      <p>Application information</p>
                    </div>
                  </div>
                  <div className="GPOLYGENESIS-settings-section-content">
                    <div className="GPOLYGENESIS-setting-row">
                      <span className="GPOLYGENESIS-setting-label">Application</span>
                      <span className="GPOLYGENESIS-setting-value">G-Polygenesis</span>
                    </div>
                    <div className="GPOLYGENESIS-setting-row">
                      <span className="GPOLYGENESIS-setting-label">Version</span>
                      <span className="GPOLYGENESIS-setting-value">1.0.0</span>
                    </div>
                    <div className="GPOLYGENESIS-setting-row">
                      <span className="GPOLYGENESIS-setting-label">Description</span>
                      <span className="GPOLYGENESIS-setting-value">GWAS-to-PGS Generator</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'api' ? (
          <CentralApiGuide
            serviceCode={SERVICE_CODE}
            serviceName="G-Polygenesis"
            token={cookieId}
            unavailableMessage={uploadUnavailable}
          />
        ) : <div className="GPOLYGENESIS-home">
          {/* Banner Header */}
          <header className="GPOLYGENESIS-home-header">
            <div className="GPOLYGENESIS-welcome-container">
              <div className="GPOLYGENESIS-welcome-logo-wrapper">
                <img src={logoSrc} alt="G-Polygenesis Logo" className="GPOLYGENESIS-welcome-logo" />
              </div>
              <div className="GPOLYGENESIS-welcome-text">
                <h1>Welcome to G-Polygenesis</h1>
                <p>One-pass PGS generation from GWAS summary statistics</p>
              </div>
            </div>
          </header>

          {/* Content Grid */}
          <div className="GPOLYGENESIS-home-content">
            {/* Left Column - Jobs */}
            <section className="GPOLYGENESIS-left-column">
              <div className="GPOLYGENESIS-section GPOLYGENESIS-jobs-section">
                <h2>Your Jobs</h2>

                <div className="GPOLYGENESIS-jobs-filter">
                  <div className="GPOLYGENESIS-search-input">
                    <i className="fas fa-search"></i>
                    <input
                      type="text"
                      placeholder="Search jobs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">All</option>
                    <option value="pending_upload">Pending Upload</option>
                    <option value="queued">Queued</option>
                    <option value="running">Running</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                <div className="GPOLYGENESIS-jobs-list">
                  {isLoadingJobs ? (
                    <div className="GPOLYGENESIS-empty-state">
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Loading...</span>
                    </div>
                  ) : filteredJobs.length === 0 ? (
                    <div className="GPOLYGENESIS-empty-state">
                      <i className="fas fa-folder-open"></i>
                      <span>No jobs found</span>
                    </div>
                  ) : (
                    filteredJobs.map(job => (
                      <div
                        key={job.uuid}
                        className={`GPOLYGENESIS-job-item ${selectedJob?.uuid === job.uuid ? 'selected' : ''}`}
                        onClick={() => setSelectedJob(job)}
                      >
                        <div className="GPOLYGENESIS-job-icon">
                          <i className="fas fa-file-alt"></i>
                        </div>
                        <div className="GPOLYGENESIS-job-content">
                          <div className="GPOLYGENESIS-job-title">{job.name}</div>
                          <div className="GPOLYGENESIS-job-meta">{getStatusMessage(job)}</div>
                        </div>
                        {job.status === 'completed' && (
                          <button
                            className="GPOLYGENESIS-job-download"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(job);
                            }}
                            title="Download results"
                          >
                            <i className="fas fa-download"></i>
                          </button>
                        )}
                        <div className={`GPOLYGENESIS-job-status status-${job.status}`}>
                          {job.status === 'completed' && <i className="fas fa-check-circle"></i>}
                          {job.status === 'running' && <i className="fas fa-spinner fa-spin"></i>}
                          {job.status === 'queued' && <i className="fas fa-clock"></i>}
                          {job.status === 'pending_upload' && <i className="fas fa-cloud-upload-alt"></i>}
                          {job.status === 'failed' && <i className="fas fa-times-circle"></i>}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>

            {/* Right Column - Upload */}
            <aside className="GPOLYGENESIS-right-column">
              <div className="GPOLYGENESIS-section GPOLYGENESIS-upload-section">
                <h2>Upload Files</h2>

                {uploadUnavailable && (
                  <div className="GPOLYGENESIS-api-note">{uploadUnavailable}</div>
                )}

                {/* Drop Zone */}
                <div
                  className={`GPOLYGENESIS-dropzone ${isDragging ? 'dragging' : ''} ${uploadedFiles.length > 0 ? 'has-files' : ''}`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".tsv,.tsv.gz,.txt,.txt.gz,.csv,.csv.gz,.gz"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <div className="GPOLYGENESIS-dropzone-content">
                    <div className="GPOLYGENESIS-dropzone-icon">
                      <i className="fas fa-cloud-upload-alt"></i>
                    </div>
                    <h3>Drop your GWAS files here</h3>
                    <p>or click to browse</p>
                    <span className="GPOLYGENESIS-dropzone-hint">TSV, TXT, CSV, GZ</span>
                  </div>
                </div>

                {/* Annotation Chips - only show when no files selected */}
                {uploadedFiles.length === 0 && (
                  <div className="GPOLYGENESIS-annotations">
                    <div className="GPOLYGENESIS-annotations-header">
                      <i className="fas fa-tags"></i>
                      <span>Outputs included:</span>
                    </div>
                    <div className="GPOLYGENESIS-annotation-chips">
                      <span className="GPOLYGENESIS-chip">PGS weights</span>
                      <span className="GPOLYGENESIS-chip">Matched SNPs</span>
                      <span className="GPOLYGENESIS-chip">Allele harmonization</span>
                      <span className="GPOLYGENESIS-chip">Model metadata</span>
                      <span className="GPOLYGENESIS-chip">Run log</span>
                      <span className="GPOLYGENESIS-chip">Download bundle</span>
                    </div>
                  </div>
                )}

                {/* File List */}
                {uploadedFiles.length > 0 && (
                  <div className="GPOLYGENESIS-file-list">
                    <h3><i className="fas fa-list"></i> Files to Upload ({uploadedFiles.length})</h3>
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="GPOLYGENESIS-file-item">
                        <div className="GPOLYGENESIS-file-icon">
                          <i className="fas fa-file-alt"></i>
                        </div>
                        <div className="GPOLYGENESIS-file-info">
                          <span className="GPOLYGENESIS-file-name">{file.name}</span>
                          <span className="GPOLYGENESIS-file-size">{formatFileSize(file.size)}</span>
                        </div>
                        <button className="GPOLYGENESIS-file-remove" onClick={() => removeFile(index)}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Progress */}
                {isUploading && (
                  <div className="GPOLYGENESIS-upload-progress">
                    <div className="GPOLYGENESIS-progress-bar">
                      <div className="GPOLYGENESIS-progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                    </div>
                    <span className="GPOLYGENESIS-progress-text">{Math.round(uploadProgress)}% uploaded</span>
                  </div>
                )}

                {/* Submit Button */}
                {uploadedFiles.length > 0 && !isUploading && (
                  <div className="GPOLYGENESIS-submit-section">
                    <button
                      className="GPOLYGENESIS-submit-btn"
                      onClick={handleSubmit}
                      disabled={!pipeline || Boolean(uploadUnavailable)}
                    >
                      <i className="fas fa-rocket"></i>
                      Start Analysis
                    </button>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>}
      </main>

      {/* Cookie Badge */}
      <div className="GPOLYGENESIS-cookie-icon">
        <i className="fas fa-cookie-bite"></i>
        <span className="GPOLYGENESIS-tooltip">{`ID: ${cookieId}`}</span>
      </div>
    </div>
  );
}

export default GPolygenesis;
